import { Api } from '@/utils/Api'
import setEntities from '@/utils/SetEntities'
import UrlStore from '@/utils/UrlStore'
import { normalize, schema } from 'normalizr'
import Vue from 'vue'
import { extractEmployees } from '@/store/modules/employee'

export const DESKS_LOAD = 'DESKS_LOAD'
export const DESK_LOAD = 'DESK_LOAD'
export const DESK_CREATE = 'DESK_CREATE'
export const DESK_UPDATE = 'DESK_UPDATE'
export const DESK_DESTROY = 'DESK_DESTROY'
export const DESK_STATE_UPDATE = 'DESK_STATE_UPDATE'

export const DESK_EVENTS_LOAD = 'DESK_EVENTS_LOAD'
export const DESK_EVENT_CREATE = 'DESK_EVENT_CREATE'
export const DESK_EVENT_UPDATE = 'DESK_EVENT_UPDATE'
export const DESK_EVENT_DESTROY = 'DESK_EVENT_DESTROY'

const desksApi = new Api(UrlStore.DESKS)
const deskEventsApi = new Api(UrlStore.DESK_EVENTS)

const deskEventSchema = new schema.Entity('deskEvents')
const deskSchema = new schema.Entity('desks', {
  deskEvents: [deskEventSchema]
})

export const extractDesks = (context, items) => {
  const desks = []
  items.forEach((item) => {
    if (item.desk) {
      desks.push(item.desk)
      item.desk = item.desk.id
    }
  })
  context.commit('desk/' + DESKS_LOAD, { items: desks }, { root: true })
}

export default {
  namespaced: true,
  state: {
    desksLoaded: false,
    desksList: [],
    deskEventsList: [],
    entities: {
      desks: {},
      deskEvents: {}
    }
  },
  getters: {
    desks(state) {
      return state.desksList.map((bId) => state.entities.desks[bId])
    },
    deskEvents(state) {
      return state.deskEventsList.map((bId) => state.entities.deskEvents[bId])
    },
    getDesk: (state) => (deskId) => {
      return state.entities.desks[deskId]
    },
    getDeskEvent: (state) => (eventId) => {
      return state.entities.deskEvents[eventId]
    }
  },
  mutations: {
    [DESKS_LOAD](state, payload) {
      const normData = normalize(payload.items, [deskSchema])
      state.desksList = normData.result
      setEntities(state, normData)
    },
    [DESK_LOAD](state, payload) {
      const normData = normalize(payload, deskSchema)
      setEntities(state, normData)
    },
    [DESK_CREATE](state, desk) {
      state.desksList.unshift(desk.id)
      Vue.set(state.entities.desks, desk.id, desk)
    },
    [DESK_UPDATE](state, desk) {
      if (desk.id && state.entities.desks[desk.id]) {
        state.entities.desks[desk.id] = Object.assign({}, state.entities.desks[desk.id], desk)
      }
    },
    [DESK_DESTROY](state, desk) {
      const num = state.desksList.findIndex((cId) => cId === desk.id)
      if (num !== -1) {
        state.desksList.splice(num, 1)
        delete state.entities.desks[desk.id]
      }
    },
    [DESK_STATE_UPDATE](state, data) {
      for (let name of Object.keys(data)) {
        state[name] = data[name]
      }
    },

    [DESK_EVENTS_LOAD](state, payload) {
      let normData = normalize(payload.items, [deskEventSchema])
      state.deskEventsList = normData.result
      setEntities(state, normData)
    },
    [DESK_EVENT_CREATE](state, deskEvent) {
      Vue.set(state.entities.deskEvents, deskEvent.id, deskEvent)
    },
    [DESK_EVENT_UPDATE](state, deskEvent) {
      if (deskEvent.id && state.entities.deskEvents[deskEvent.id]) {
        state.entities.deskEvents[deskEvent.id] = Object.assign({}, state.entities.deskEvents[deskEvent.id], deskEvent)
      }
    },
    [DESK_EVENT_DESTROY](state, desk) {
      delete state.entities.deskEvents[desk.id]
    }
  },
  actions: {
    desksLoad({ commit }, config = {}) {
      return desksApi.getAll(config).then((r) => {
        if (r.data.items.length) {
          let events = []
          r.data.items.forEach((desk) => {
            if (desk.deskEvents) {
              events.push(desk.deskEvents)
            }
          })
          extractEmployees({ commit }, events)
        }

        commit(DESKS_LOAD, r.data)
        commit(DESK_STATE_UPDATE, { desksLoaded: true })
      })
    },
    deskLoad({ commit }, config) {
      return desksApi.get({ id: config.id, config: config }).then((r) => {
        if (r.data.deskEvents) {
          extractEmployees({ commit }, r.data.deskEvents)
        }
        commit(DESK_LOAD, r.data)
      })
    },
    deskSave({ commit, getters }, { desk, config = {} }) {
      if (!desk.id) throw new Error('Desk id is not present in `deskSave`!')

      desk.number = desk.number !== '' ? desk.number : null

      commit(getters.getDesk(desk.id) ? DESK_UPDATE : DESK_CREATE, desk)
      const deskUpdated = getters.getDesk(desk.id)

      return desksApi.save(deskUpdated, config).then((r) => {
        if (r._isNew) {
          commit(DESK_DESTROY, deskUpdated) // local
          commit(DESK_CREATE, r.data)
          if (r.data.point_id) {
            commit(`point/POINT_UPDATE`, { id: r.data.point_id, desk: r.data.id }, { root: true })
          }
        } else {
          commit(DESK_UPDATE, r.data)
        }
        return r.data.id
      })
    },
    deskDestroy({ commit }, desk) {
      return desksApi.destroy({ id: desk.id }).then(() => {
        commit(DESK_DESTROY, desk)
      })
    },

    // just load load data and return
    deskEventsSearch(_, config) {
      return deskEventsApi.getAll(config)
    },
    // deskEventsSearch & commit data to the store
    deskEventsLoad({ commit }, config = {}) {
      return deskEventsApi.getAll(config).then((r) => {
        if (r.data.items.length) {
          extractEmployees({ commit }, r.data.items)
        }
        commit(DESK_EVENTS_LOAD, r.data)
      })
    },
    deskEventSave({ commit, getters }, { deskEvent, config = {} }) {
      if (!deskEvent.id) throw new Error('deskEvent id is not present in `deskEventSave`!')
      commit(getters.getDeskEvent(deskEvent.id) ? DESK_EVENT_UPDATE : DESK_EVENT_CREATE, deskEvent)
      let eventUpdated = getters.getDeskEvent(deskEvent.id)
      return deskEventsApi.save(eventUpdated, config).then((r) => {
        if (r._isNew) {
          commit(DESK_EVENT_DESTROY, eventUpdated) // local
          commit(DESK_EVENT_CREATE, r.data)
        } else {
          commit(DESK_EVENT_UPDATE, r.data)
        }
        return r.data.id
      })
    },
    deskEventDestroy({ commit }, deskEvent) {
      return deskEventsApi.destroy({ id: deskEvent.id }).then(() => {
        commit(DESK_EVENT_DESTROY, deskEvent)
      })
    }
  }
}
